import { Box, Dialog, DialogContent, Typography } from "@mui/material"
import type { ReactElement } from "react"
import { useMemo, useState } from "react"

import InstructionSearchSuggestion from "./InstructionSearchSuggestion"

import type { AerosTagData } from "@/components/UiKit"
import type { Instruction } from "@/graphql/codegen/graphql"
import DialogArea from "@/screens/ManageProducts/components/Instructions/components/addProduct/DialogArea"
import InstructionsArea from "@/screens/Products/components/Product/Instructions/components/AddInstruction/InstructionsArea"
import SelectedItemsArea from "@/screens/Products/components/Product/Instructions/components/SelectedItemsArea/SelectedItemsArea"
import useAddInstructionToProduct from "@/screens/Products/components/Product/Instructions/hooks/useAddInstructionToProduct"
import useInstructionTableActionsOptions from "@/screens/Products/components/Product/Instructions/hooks/useInstructionTableActionsOptions"
import useParametrizedInstructionsForProductList from "@/screens/Products/components/Product/Instructions/hooks/useParametrizedInstructionsForProductList"
import type { AutocompleteOption } from "@/types/form-fields"

interface AddInstructionDialogProps {
  productId: string
  associatedInstructionIds: string[]
  onClose: () => void
  onSuccess: () => void
}

const AddInstructionDialog = ({
  productId,
  associatedInstructionIds,
  onClose,
  onSuccess,
}: AddInstructionDialogProps): ReactElement => {
  const [searchQuery, setSearchQuery] = useState<string | AutocompleteOption>("")
  const onSearch = (searchedItem: string | AutocompleteOption) => {
    setSearchQuery(searchedItem)
  }

  const { instructionsToAssociate, selectedInstructions, selected, setSelected, sorting, setSorting } =
    useInstructionTableActionsOptions({
      associatedInstructionIds,
    })

  const { addInstructions } = useAddInstructionToProduct()
  const { instructions, fetchingInstructions } = useParametrizedInstructionsForProductList({
    searchQuery,
    sorting,
  })

  const handleSubmit = async () => {
    const success = await addInstructions({
      productId: Number(productId),
      instructionIds: instructionsToAssociate,
    })
    if (success) {
      onSuccess()
    }
  }

  // Only newly added instructions are displayed in selected area and can be removed
  const selectedTags = useMemo(() => {
    const list = [...instructions, ...selectedInstructions.current]
    const newListOfSelectedInstructions: Instruction[] = []
    const newTags = instructionsToAssociate
      .map((id) => {
        const found = list.find((i) => i.instructionId === id)
        if (!found) {
          return false
        }
        newListOfSelectedInstructions.push(found)
        const { instructionId, name } = found
        return {
          key: instructionId,
          value: instructionId,
          label: name,
        }
      })
      .filter(Boolean) as AerosTagData[]

    // Here save selected instruction data as with a new query we may lose selected items instruction data
    selectedInstructions.current = newListOfSelectedInstructions

    return newTags
  }, [instructions, instructionsToAssociate, selectedInstructions])

  const onRemoveSelectedInstruction = (tagToDelete: AerosTagData) => {
    const idToRemove = tagToDelete.value as string

    selectedInstructions.current = selectedInstructions.current.filter((i) => i.instructionId !== idToRemove)

    setSelected((prev) => {
      const { [idToRemove]: _, ...newList } = prev
      return newList as Record<string, boolean>
    })
  }

  return (
    <Dialog
      open={true}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            maxWidth: "1072px",
            width: "100%",
          },
        },
      }}
    >
      <DialogArea
        title='Add Instructions'
        onClose={onClose}
        onSubmit={handleSubmit}
        isSubmitEnabled={!!instructionsToAssociate.length}
      >
        <DialogContent classes={{ root: "p-10" }}>
          {!!instructionsToAssociate.length && (
            <SelectedItemsArea
              items={selectedTags}
              onDelete={onRemoveSelectedInstruction}
              title='Selected Instructions'
            />
          )}
          <Typography className='mb-6 text-ml font-normal text-gray-900'>
            Search instructions to be applied to the product.
          </Typography>

          <Box className='flex flex-col gap-6'>
            <InstructionSearchSuggestion onSearch={onSearch} />
            {searchQuery && (
              <InstructionsArea
                searchQuery={searchQuery}
                instructions={instructions}
                fetchingInstructions={fetchingInstructions}
                onInstructionSelection={setSelected}
                onInstructionSorted={setSorting}
                associatedInstructionIds={associatedInstructionIds}
                selected={selected}
                sorting={sorting}
              />
            )}
          </Box>
        </DialogContent>
      </DialogArea>
    </Dialog>
  )
}

export default AddInstructionDialog
